import { useState } from "react";
import { Star } from "lucide-react";
import { RESULTS, PROCESS, FAQS, REELS, PORTFOLIO, type VideoItem } from "@/lib/site";
import { Reel, VideoModal, ClickToPlay } from "./Video";
import LogoStripV2 from "@/components/v2/LogoStripV2";
import metaLogo from "@/assets/meta-logo.png";

export const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-primary">{children}</p>
);

export const TrustStrip = () => (
  <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-muted-foreground">
    <span className="flex items-center gap-2">
      <span className="flex text-primary">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-4 w-4 fill-current" />)}</span>
      Rated 5/5 on Google
    </span>
    <span className="flex items-center gap-2">
      <img src={metaLogo} alt="Meta" width={20} height={20} className="h-5 w-5 object-contain" loading="lazy" /> Meta ads specialists
    </span>
  </div>
);

export const LogoStrip = () => <LogoStripV2 />;

export const ResultsGrid = ({ dark = false }: { dark?: boolean }) => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
    {RESULTS.map((r) => (
      <div key={r.stat} className={`rounded-3xl p-8 ${dark ? "bg-background/10 ring-1 ring-background/20" : "bg-muted"}`}>
        <p className={`whitespace-nowrap text-5xl font-bold tracking-tight ${dark ? "text-primary-foreground" : "text-primary"} md:text-6xl`}>{r.stat}</p>
        <p className={`mt-2 text-xl font-semibold ${dark ? "text-primary-foreground" : "text-foreground"}`}>{r.unit}</p>
        <p className={`mt-4 text-base leading-relaxed ${dark ? "text-primary-foreground" : "text-foreground/75"}`}>{r.label}</p>
      </div>
    ))}
  </div>
);

export const ProcessSteps = ({ circles = false }: { circles?: boolean }) => circles ? (
  <ol className="relative grid gap-10 md:grid-cols-5 md:gap-6">
    <span aria-hidden className="absolute left-[10%] right-[10%] top-6 hidden h-0.5 bg-primary/40 md:block" />
    {PROCESS.map((s, i) => (
      <li key={s.title} className="relative text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground ring-8 ring-background">{i + 1}</span>
        <h3 className="mt-5 text-xl font-semibold">{s.title}</h3>
        <p className="mt-2 text-foreground/70">{s.text}</p>
      </li>
    ))}
  </ol>
) : (
  <ol className="grid gap-6 md:grid-cols-5">
    {PROCESS.map((s, i) => (
      <li key={s.title} className="border-t-2 border-primary pt-5">
        <span className="text-sm font-semibold text-primary">0{i + 1}</span>
        <h3 className="mt-2 text-xl font-semibold">{s.title}</h3>
        <p className="mt-2 text-foreground/70">{s.text}</p>
      </li>
    ))}
  </ol>
);

export { default as Reviews } from "@/components/GoogleReviews";

export const Faq = ({ items = FAQS }: { items?: { q: string; a: string }[] }) => (
  <div className="divide-y divide-border">
    {items.map((f) => (
      <details key={f.q} className="group py-6">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-lg font-semibold">
          {f.q}
          <span className="text-2xl text-primary transition-transform group-open:rotate-45">+</span>
        </summary>
        <p className="mt-3 text-foreground/70">{f.a}</p>
      </details>
    ))}
  </div>
);

export const ReelCarousel = () => {
  // Shuffle once per load, then duplicate for a seamless loop.
  const [shuffled] = useState(() => [...REELS].sort(() => Math.random() - 0.5));
  const loop = [...shuffled, ...shuffled];
  return (
    <div
      className="relative overflow-hidden"
      style={{
        maskImage: "linear-gradient(to right, transparent, black 4%, black 96%, transparent)",
        WebkitMaskImage: "linear-gradient(to right, transparent, black 4%, black 96%, transparent)",
      }}
    >
      <div className="reels-track gap-5 px-6" style={{ animationDuration: "90s" }}>
        {loop.map((r, i) => <Reel key={`${r.id}-${i}`} {...r} />)}
      </div>
    </div>
  );
};

export const PortfolioGrid = ({ filterable = false }: { filterable?: boolean }) => {
  const cats = ["All", ...Array.from(new Set(PORTFOLIO.map((p) => p.category)))];
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState<VideoItem | null>(null);
  const linked = PORTFOLIO.filter((p) => p.youtubeId || p.src);
  const items = cat === "All" ? linked : linked.filter((p) => p.category === cat);
  return (
    <>
      {filterable && (
        <div className="mb-8 flex flex-wrap gap-2" role="tablist">
          {cats.map((c) => (
            <button key={c} role="tab" aria-selected={cat === c} onClick={() => setCat(c)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${cat === c ? "bg-primary text-primary-foreground" : "bg-muted text-foreground hover:bg-primary/10"}`}>
              {c}
            </button>
          ))}
        </div>
      )}
      <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2">
        {items.map((v) => (
          <div key={v.id}>
            <div onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(v); }}><ClickToPlay video={v} /></div>
            <p className="mt-4 font-semibold">{v.title}</p>
          </div>
        ))}
      </div>
      <VideoModal video={open} onClose={() => setOpen(null)} />
    </>
  );
};
